/**
 * Recovery Flow Component
 * Manages the display of recovery key modal when needed
 * Integrates device trust automation after successful recovery
 */

import { useEffect, useCallback, useState } from 'react';
import RecoveryKeyModal from './RecoveryKeyModal';
import { useRecovery, useRecoveryActions, useShowRecoveryModal } from '../../context/RecoveryContext/index';
import { useMatrixClient } from '../../context/MatrixContext';
import RecoveryKeyValidator from '../../services/RecoveryKeyValidator';
import { isRecoveryFlowEnabled, isDeviceTrustAutomationEnabled } from '../../config/featureFlags';
import deviceTrustManager from '../../crypto/DeviceTrustManager';
import VerificationModal from '../verification/VerificationModal';
import logger from '../../utils/logger';

/**
 * RecoveryFlow component handles the recovery key modal display
 * and integrates with the recovery context and validator service
 */
const RecoveryFlow = () => {
  const client = useMatrixClient();
  const recoveryState = useRecovery();
  const recoveryActions = useRecoveryActions();
  const shouldShowModal = useShowRecoveryModal();
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [deviceTrustInProgress, setDeviceTrustInProgress] = useState(false);

  // Check if features are enabled
  const isEnabled = isRecoveryFlowEnabled();
  const isDeviceTrustEnabled = isDeviceTrustAutomationEnabled();

  useEffect(() => {
    if (shouldShowModal && isEnabled) {
      logger.debug('[RecoveryFlow] Recovery modal should be shown', {
        step: recoveryState.step,
        needsRecovery: recoveryState.needsRecovery,
      });
    }
  }, [shouldShowModal, isEnabled, recoveryState.step, recoveryState.needsRecovery]);

  /**
   * Handle device trust automation after recovery
   */
  const handleDeviceTrustAutomation = useCallback(async () => {
    if (!isDeviceTrustEnabled) {
      logger.debug('[RecoveryFlow] Device trust automation is disabled');
      return;
    }

    logger.info('[RecoveryFlow] Starting device trust automation');
    setDeviceTrustInProgress(true);

    try {
      // Run device trust automation
      const result = await deviceTrustManager.runAfterRecovery();

      logger.debug('[RecoveryFlow] Device trust result:', result);

      if (result.fallbackRequired) {
        logger.info('[RecoveryFlow] Fallback verification required');
        // Show verification modal for manual verification
        setShowVerificationModal(true);
      } else if (result.trusted) {
        logger.info('[RecoveryFlow] Device successfully trusted');
      }
    } catch (error) {
      logger.error('[RecoveryFlow] Device trust automation failed:', error);
      // Non-critical error, don't block recovery flow
    } finally {
      setDeviceTrustInProgress(false);
    }
  }, [isDeviceTrustEnabled]);

  /**
   * Handle successful recovery key submission
   */
  const handleSuccess = useCallback(async () => {
    logger.info('[RecoveryFlow] Recovery key validated successfully');

    // The validator has already stored the key
    // Now trigger the recovery flow actions
    if (recoveryActions.restore) {
      try {
        await recoveryActions.restore();

        // After successful restore, run device trust automation
        await handleDeviceTrustAutomation();
      } catch (error) {
        logger.error('[RecoveryFlow] Failed to restore after validation:', error);
      }
    }
  }, [recoveryActions, handleDeviceTrustAutomation]);

  /**
   * Handle skip recovery
   */
  const handleSkip = useCallback(() => {
    logger.info('[RecoveryFlow] User skipped recovery');

    if (recoveryActions.skip) {
      recoveryActions.skip();
    }
  }, [recoveryActions]);

  /**
   * Handle reset backup
   */
  const handleReset = useCallback(async () => {
    logger.info('[RecoveryFlow] User requested backup reset');

    if (recoveryActions.reset) {
      try {
        await recoveryActions.reset();
      } catch (error) {
        logger.error('[RecoveryFlow] Failed to reset backup:', error);
      }
    }
  }, [recoveryActions]);

  /**
   * Handle verification modal completion
   */
  const handleVerificationComplete = useCallback((success) => {
    logger.info('[RecoveryFlow] Verification modal completed:', success);
    setShowVerificationModal(false);
  }, []);

  // Create validator configuration
  const validator = {
    onSubmit: async (recoveryKey) => {
      try {
        // Use RecoveryKeyValidator to validate and store the key
        const result = await RecoveryKeyValidator.validateAndSubmit(
          recoveryKey,
          client,
          recoveryActions.dispatch,
        );

        return result;
      } catch (error) {
        logger.error('[RecoveryFlow] Validation error:', error);
        return {
          success: false,
          error: error.code || 'UNKNOWN',
        };
      }
    },
  };

  // Don't render if feature is disabled
  if (!isEnabled) {
    return null;
  }

  // Don't render if modal shouldn't be shown
  if (!shouldShowModal) {
    return null;
  }

  return (
    <>
      {shouldShowModal && (
        <RecoveryKeyModal
          validator={validator}
          onSuccess={handleSuccess}
          onSkip={handleSkip}
          onReset={handleReset}
          allowSkip={true}
          allowReset={true}
        />
      )}

      {showVerificationModal && (
        <VerificationModal
          isOpen={showVerificationModal}
          onClose={() => setShowVerificationModal(false)}
          onComplete={handleVerificationComplete}
        />
      )}

      {deviceTrustInProgress && (
        <div className="device-trust-indicator">
          Verifying device...
        </div>
      )}
    </>
  );
};

export default RecoveryFlow;